import { TMDbMovie } from '../services/tmdbApi';
import {
  getStandardizedMovieId,
  areMovieIdsEquivalent,
  migrateMovieId,
  extractCoreMovieId,
  normalizeMovieObject,
  getTmdbNumericIdForCache,
  getTmdbNumericIdFromMovieRecord,
  resolveTmdbQueueMovieId,
} from '@watchlist/shared';

export {
  getStandardizedMovieId,
  areMovieIdsEquivalent,
  migrateMovieId,
  extractCoreMovieId,
  normalizeMovieObject,
  getTmdbNumericIdForCache,
  getTmdbNumericIdFromMovieRecord,
  resolveTmdbQueueMovieId,
};

/**
 * True when the route param (e.g. tt*, tmdb_*, numeric) refers to the same film as the loaded TMDB payload.
 * Used so parallel Firestore fetches keyed by the URL id still hydrate when the canonical id differs (tt vs tmdb_*).
 */
export const movieRouteMatchesLoadedMovie = (routeMovieId: string, movie: TMDbMovie | any): boolean => {
  if (!routeMovieId || routeMovieId === 'unknown') return false;
  const std = getStandardizedMovieId(movie);
  if (!std || std === 'unknown') return false;
  if (routeMovieId === std) return true;
  if (areMovieIdsEquivalent(routeMovieId, std)) return true;

  const imdbOnMovie =
    movie.imdb_id ||
    (typeof movie.imdbID === 'string' && /^tt\d+$/.test(movie.imdbID.trim()) ? movie.imdbID.trim() : undefined);
  if (imdbOnMovie && routeMovieId === imdbOnMovie) return true;

  const tmdbNumeric = movie.id != null ? String(movie.id).trim() : '';
  if (tmdbNumeric && /^\d+$/.test(tmdbNumeric)) {
    if (routeMovieId === `tmdb_${tmdbNumeric}` || routeMovieId === tmdbNumeric) return true;
  }

  return false;
};

/**
 * @deprecated Use WatchlistFilteringService instead for consistent filtering across all components
 * This function is kept for backward compatibility but will be removed in a future version
 */
/**
 * @deprecated Use WatchlistFilteringService.getFilteredMovies() instead for consistent filtering across all components
 * This function is kept for backward compatibility but will be removed in a future version
 */
export const getFilteredWatchlistMovies = (
  watchlistType: string,
  activeTab: number,
  watchlistMovies: any[],
  getUserMovieData: (movieId: string) => any,
  getWatchlistById: (id: string) => any
): any[] => {
  console.warn('⚠️ getFilteredWatchlistMovies is deprecated. Use WatchlistFilteringService.getFilteredMovies() instead.');
  console.warn('⚠️ getFilteredWatchlistMovies is deprecated. Use WatchlistFilteringService instead.');
  
  // Legacy implementation kept for backward compatibility
  // New code should use WatchlistFilteringService.getFilteredMovies()
  let filteredMovies = [...watchlistMovies];
  
  const userWatchlist = getWatchlistById(watchlistType);
  
  if (watchlistType === 'want-to-watch') {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      return movieData && movieData.isInWatchlist && !movieData.isWatched;
    });
    return filteredMovies;
  }
  
  if (watchlistType === 'watched') {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      return movieData?.isWatched === true;
    });
    return filteredMovies;
  }
  
  if (watchlistType === 'your-reviews') {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      return movieData?.isWatched === true && (movieData?.review || movieData?.rating);
    });
    return filteredMovies;
  }
  
  if (activeTab === 1) {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      
      if (userWatchlist) {
        return !movieData?.isWatched;
      } else if (movieData) {
        return movieData.isInWatchlist && !movieData.isWatched;
      } else {
        return false;
      }
    });
  } else if (activeTab === 2) {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      return movieData?.isWatched === true;
    });
  } else {
    filteredMovies = watchlistMovies.filter(movie => {
      const movieId = getStandardizedMovieId(movie);
      const movieData = getUserMovieData(movieId);
      
      if (userWatchlist) {
        return true;
      } else if (movieData) {
        return movieData.isWatched || movieData.isInWatchlist;
      } else {
        return false;
      }
    });
  }
  
  return filteredMovies;
};

/**
 * @deprecated Use WatchlistFilteringService.getMovieCount() instead
 * This function contains INCORRECT LOGIC for your-reviews (includes ratings, should only be reviews)
 * This function is kept for backward compatibility but will be removed in a future version
 * DO NOT USE - Use WatchlistFilteringService for correct filtering logic
 */
export const getActualMovieCount = (
  watchlistType: string,
  activeTab: number,
  getUserMovieData: (movieId: string) => any,
  getWatchlistById: (id: string) => any,
  getWatchlistMovieIds: () => string[],
  getWatchedMovieIds: () => string[],
  getReviewedMovieIds: () => string[],
  getWantToWatchCount: () => number
): number => {
  console.warn('⚠️ getActualMovieCount is deprecated. Use WatchlistFilteringService.getMovieCount() instead.');
  
  // Legacy implementation kept for backward compatibility
  // New code should use WatchlistFilteringService.getMovieCount()
  const userWatchlist = getWatchlistById(watchlistType);
  let watchlistMovies: any[] = [];
  
  if (watchlistType === 'want-to-watch' || watchlistType === 'main-watchlist') {
    const movieIds = getWatchlistMovieIds();
    watchlistMovies = movieIds.map(id => ({ imdbID: id, id: id }));
  } else if (watchlistType === 'watched') {
    const movieIds = getWatchedMovieIds();
    watchlistMovies = movieIds.map(id => ({ imdbID: id, id: id }));
  } else if (watchlistType === 'your-reviews') {
    const movieIds = getReviewedMovieIds();
    watchlistMovies = movieIds.map(id => ({ imdbID: id, id: id }));
  } else if (userWatchlist && userWatchlist.movieDetails) {
    watchlistMovies = userWatchlist.movieDetails;
  } else {
    const movieCounts: { [key: string]: number } = {
      'classics': 12,
      'villeneuve': 8,
      'ghibli': 10,
      'comedy': 12,
    };
    return movieCounts[watchlistType] || 0;
  }
  
  const filteredMovies = getFilteredWatchlistMovies(
    watchlistType,
    activeTab,
    watchlistMovies,
    getUserMovieData,
    getWatchlistById
  );
  
  return filteredMovies.length;
};

/**
 * Test function to verify ID standardization works correctly
 * Can be called from browser console: testIdStandardization()
 */
export const testIdStandardization = () => {
  console.log('🧪 Testing ID Standardization...');
  
  const testCases = [
    // TMDB ID cases (should be prioritized)
    { input: { id: '335984' }, expected: 'tmdb_335984', description: 'Numeric TMDB ID' },
    { input: { id: '335984', imdbID: 'tt1856101' }, expected: 'tmdb_335984', description: 'TMDB ID with IMDb ID (TMDB should win)' },
    { input: { id: 'tmdb_335984' }, expected: 'tmdb_335984', description: 'Already formatted TMDB ID' },

    // Double-prefix cases (critical bug fix)
    { input: { id: 'tmdb_tmdb_335984' }, expected: 'tmdb_335984', description: 'Double-prefixed TMDB ID' },
    { input: { id: 'tmdb_tmdb_tmdb_335984' }, expected: 'tmdb_335984', description: 'Triple-prefixed TMDB ID' },
    { input: { imdbID: 'tmdb_tmdb_1084242' }, expected: 'tmdb_1084242', description: 'Double-prefixed in imdbID field' },

    // Old format cases
    { input: { id: 'tmdb335984' }, expected: 'tmdb_335984', description: 'Old format without underscore' },

    // IMDb ID cases (fallback)
    { input: { imdbID: 'tt1856101' }, expected: 'tt1856101', description: 'Pure IMDb ID' },
    { input: { imdbID: 'tmdb_335984' }, expected: 'tmdb_335984', description: 'TMDB ID disguised as IMDb ID' },

    // Title-based fallback
    { input: { Title: 'Blade Runner 2049', Year: '2017' }, expected: 'title_bladerunner2049_2017', description: 'Title-based ID' },
  ];
  
  let passed = 0;
  let failed = 0;
  
  testCases.forEach((testCase, index) => {
    const result = getStandardizedMovieId(testCase.input);
    const success = result === testCase.expected;
    
    console.log(`${success ? '✅' : '❌'} Test ${index + 1}: ${testCase.description}`);
    console.log(`  Input: ${JSON.stringify(testCase.input)}`);
    console.log(`  Expected: ${testCase.expected}`);
    console.log(`  Got: ${result}`);
    
    if (success) passed++;
    else failed++;
  });
  
  console.log(`\n📊 Results: ${passed} passed, ${failed} failed`);
  
  if (failed === 0) {
    console.log('🎉 All tests passed! ID standardization is working correctly.');
  } else {
    console.log('⚠️ Some tests failed. ID standardization needs attention.');
  }
  
  return { passed, failed, total: testCases.length };
};

// Expose test function globally for easy browser console access
if (typeof window !== 'undefined') {
  (window as any).testIdStandardization = testIdStandardization;
} 

/**
 * Comprehensive duplicate cleanup utility for immediate use
 * This function identifies and removes duplicate movies from the user's data
 */
export const cleanupDuplicateMovies = async (
  movieDataCache: Map<string, any>,
  removeFromWatchlist: (movieId: string) => Promise<void>,
  refreshMovieData: (movieId: string) => Promise<void>
): Promise<{ cleanupCount: number; duplicateGroups: number }> => {
  console.log('🧹 movieUtils: Starting comprehensive duplicate cleanup...');
  
  if (!movieDataCache || movieDataCache.size === 0) {
    console.log('🚫 movieUtils: No movie data to cleanup');
    return { cleanupCount: 0, duplicateGroups: 0 };
  }
  
  const currentMovies = Array.from(movieDataCache.values());
  const duplicateGroups = new Map<string, any[]>();
  
  // Group movies by normalized identifiers to find duplicates
  currentMovies.forEach(movieData => {
    if (!movieData.movieId) return;
    
    let normalizedKey = movieData.movieId;
    
    // Normalize different ID formats to find duplicates
    if (movieData.movieId.startsWith('tt')) {
      normalizedKey = movieData.movieId; // IMDb IDs are canonical
    } else if (movieData.movieId.startsWith('tmdb_')) {
      normalizedKey = `tmdb_${movieData.movieId.replace('tmdb_', '')}`;
    } else if (movieData.movieId.startsWith('tmdb') && !movieData.movieId.startsWith('tmdb_')) {
      normalizedKey = `tmdb_${movieData.movieId.replace('tmdb', '')}`;
    } else if (movieData.movieId.startsWith('title_')) {
      // For title-based IDs, extract the core title
      const parts = movieData.movieId.split('_');
      normalizedKey = parts.slice(1, -1).join('_'); // Remove 'title_' prefix and year suffix
    }
    
    if (!duplicateGroups.has(normalizedKey)) {
      duplicateGroups.set(normalizedKey, []);
    }
    duplicateGroups.get(normalizedKey)!.push(movieData);
  });
  
  // Find and fix duplicate groups
  let cleanupCount = 0;
  let duplicateGroupCount = 0;
  
  for (const [normalizedKey, duplicates] of Array.from(duplicateGroups.entries())) {
    if (duplicates.length > 1) {
      duplicateGroupCount++;
      console.log(`🔍 movieUtils: Found ${duplicates.length} duplicates for ${normalizedKey}:`, 
        duplicates.map(d => d.movieId));
      
      // Find the best duplicate (prefer IMDb IDs, then most complete data)
      let bestDuplicate = duplicates[0];
      let bestScore = 0;
      
      for (const duplicate of duplicates) {
        let score = 0;
        
        // Prefer proper IMDb IDs (most reliable)
        if (duplicate.movieId.startsWith('tt')) score += 20;
        else if (duplicate.movieId.startsWith('tmdb_')) score += 10;
        
        // Score based on data completeness
        if (duplicate.isWatched) score += 10;
        if (duplicate.rating) score += 5;
        if (duplicate.review) score += 5;
        if (duplicate.isInWatchlist) score += 3;
        if (duplicate.watchedDate) score += 2;
        
        if (score > bestScore) {
          bestScore = score;
          bestDuplicate = duplicate;
        }
      }
      
      console.log(`✅ movieUtils: Selected best duplicate for ${normalizedKey}:`, 
        `${bestDuplicate.movieId} (score: ${bestScore})`);
      
      try {
        // Remove all duplicates except the best one
        for (const duplicate of duplicates) {
          if (duplicate.movieId !== bestDuplicate.movieId) {
            console.log(`🗑️ movieUtils: Removing duplicate ${duplicate.movieId}`);
            await removeFromWatchlist(duplicate.movieId);
            cleanupCount++;
          }
        }
        
        // Refresh the best duplicate to ensure it has merged data
        await refreshMovieData(bestDuplicate.movieId);
        
        console.log(`✅ movieUtils: Successfully cleaned up duplicates for ${normalizedKey}`);
        
      } catch (error) {
        console.error(`❌ movieUtils: Error cleaning up duplicates for ${normalizedKey}:`, error);
      }
    }
  }
  
  console.log(`🎯 movieUtils: Cleanup completed. Removed ${cleanupCount} duplicates from ${duplicateGroupCount} groups`);
  
  return { cleanupCount, duplicateGroups: duplicateGroupCount };
};

/**
 * Quick duplicate detection function for debugging
 */
export const findDuplicateMovies = (movieDataCache: Map<string, any>): Array<{
  normalizedKey: string;
  duplicates: any[];
}> => {
  if (!movieDataCache || movieDataCache.size === 0) {
    return [];
  }
  
  const currentMovies = Array.from(movieDataCache.values());
  const duplicateGroups = new Map<string, any[]>();
  
  // Group movies by normalized identifiers
  currentMovies.forEach(movieData => {
    if (!movieData.movieId) return;
    
    let normalizedKey = movieData.movieId;
    
    // Normalize different ID formats
    if (movieData.movieId.startsWith('tt')) {
      normalizedKey = movieData.movieId;
    } else if (movieData.movieId.startsWith('tmdb_')) {
      normalizedKey = `tmdb_${movieData.movieId.replace('tmdb_', '')}`;
    } else if (movieData.movieId.startsWith('tmdb') && !movieData.movieId.startsWith('tmdb_')) {
      normalizedKey = `tmdb_${movieData.movieId.replace('tmdb', '')}`;
    } else if (movieData.movieId.startsWith('title_')) {
      const parts = movieData.movieId.split('_');
      normalizedKey = parts.slice(1, -1).join('_');
    }
    
    if (!duplicateGroups.has(normalizedKey)) {
      duplicateGroups.set(normalizedKey, []);
    }
    duplicateGroups.get(normalizedKey)!.push(movieData);
  });
  
  // Return only groups with duplicates
  const duplicates: Array<{ normalizedKey: string; duplicates: any[] }> = [];
  for (const [normalizedKey, group] of Array.from(duplicateGroups.entries())) {
    if (group.length > 1) {
      duplicates.push({ normalizedKey, duplicates: group });
    }
  }
  
  return duplicates;
}; 

/**
 * Comprehensive watchlist duplicate cleanup for immediate use
 * This function identifies and cleans duplicate movies from watchlist data at the source
 */
export const cleanupWatchlistDuplicates = (movieDetails: any[]): {
  cleanedMovies: any[];
  duplicatesRemoved: number;
  duplicateInfo: Array<{ originalId: string; standardizedId: string; action: string }>;
} => {
  console.log('🧹 movieUtils: Starting watchlist duplicate cleanup...');
  
  if (!movieDetails || movieDetails.length === 0) {
    console.log('🚫 movieUtils: No movie details to cleanup');
    return { cleanedMovies: [], duplicatesRemoved: 0, duplicateInfo: [] };
  }
  
  const processedIds = new Set<string>();
  const cleanedMovies: any[] = [];
  const duplicateInfo: Array<{ originalId: string; standardizedId: string; action: string }> = [];
  let duplicatesRemoved = 0;
  
  movieDetails.forEach((movie, index) => {
    const standardizedId = getStandardizedMovieId(movie);
    const originalId = movie.imdbID || movie.id || 'unknown';
    
    if (!standardizedId || standardizedId === 'unknown') {
      duplicateInfo.push({
        originalId,
        standardizedId,
        action: 'SKIPPED_INVALID_ID'
      });
      console.warn('movieUtils: Skipping movie with invalid ID:', movie);
      return;
    }
    
    if (processedIds.has(standardizedId)) {
      duplicateInfo.push({
        originalId,
        standardizedId,
        action: 'REMOVED_DUPLICATE'
      });
      duplicatesRemoved++;
      console.warn('movieUtils: Removing duplicate movie:', { 
        title: movie.title || movie.Title, 
        originalId, 
        standardizedId,
        position: index
      });
      return;
    }
    
    processedIds.add(standardizedId);
    cleanedMovies.push(movie);
    duplicateInfo.push({
      originalId,
      standardizedId,
      action: 'KEPT'
    });
  });
  
  console.log('🎯 movieUtils: Duplicate cleanup completed:', {
    originalCount: movieDetails.length,
    cleanedCount: cleanedMovies.length,
    duplicatesRemoved,
    duplicateInfo: duplicateInfo.filter(info => info.action !== 'KEPT')
  });
  
  return { cleanedMovies, duplicatesRemoved, duplicateInfo };
};

/**
 * Enhanced movie deduplication with detailed logging for debugging
 */
export const analyzeMovieListForDuplicates = (movieDetails: any[]): {
  hasDuplicates: boolean;
  duplicateGroups: Array<{
    standardizedId: string;
    movies: Array<{ 
      originalId: string; 
      title: string; 
      index: number;
      formats: string[];
    }>;
  }>;
  analysis: {
    totalMovies: number;
    uniqueMovies: number;
    duplicateCount: number;
    idFormatVariations: string[];
  };
} => {
  console.log('🔍 movieUtils: Analyzing movie list for duplicates...');
  
  if (!movieDetails || movieDetails.length === 0) {
    return {
      hasDuplicates: false,
      duplicateGroups: [],
      analysis: {
        totalMovies: 0,
        uniqueMovies: 0,
        duplicateCount: 0,
        idFormatVariations: []
      }
    };
  }
  
  const idGroups = new Map<string, Array<{
    originalId: string;
    title: string;
    index: number;
    formats: string[];
  }>>();
  
  const allIdFormats = new Set<string>();
  
  movieDetails.forEach((movie, index) => {
    const standardizedId = getStandardizedMovieId(movie);
    const originalId = movie.imdbID || movie.id || 'unknown';
    const title = movie.title || movie.Title || 'Unknown';
    
    if (!standardizedId || standardizedId === 'unknown') {
      return;
    }
    
    // Track ID format variations
    const idFormat = originalId.startsWith('tt') ? 'imdb' :
                    originalId.startsWith('tmdb_') ? 'tmdb_prefixed' :
                    originalId.startsWith('tmdb') ? 'tmdb_old' :
                    /^\d+$/.test(originalId) ? 'numeric' : 'other';
    allIdFormats.add(idFormat);
    
    if (!idGroups.has(standardizedId)) {
      idGroups.set(standardizedId, []);
    }
    
    idGroups.get(standardizedId)!.push({
      originalId,
      title,
      index,
      formats: [idFormat]
    });
  });
  
  // Find duplicate groups
  const duplicateGroups = Array.from(idGroups.entries())
    .filter(([_, movies]) => movies.length > 1)
    .map(([standardizedId, movies]) => ({
      standardizedId,
      movies
    }));
  
  const uniqueCount = idGroups.size;
  const duplicateCount = movieDetails.length - uniqueCount;
  
  const analysis = {
    totalMovies: movieDetails.length,
    uniqueMovies: uniqueCount,
    duplicateCount,
    idFormatVariations: Array.from(allIdFormats)
  };
  
  console.log('📊 movieUtils: Duplicate analysis results:', {
    analysis,
    duplicateGroupCount: duplicateGroups.length,
    duplicateGroups: duplicateGroups.length > 0 ? duplicateGroups : 'none'
  });
  
  return {
    hasDuplicates: duplicateGroups.length > 0,
    duplicateGroups,
    analysis
  };
}; 